import { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Input,
  Image,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
 
import { qaApi } from "services/qaApi";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import IconButton from "components/common/IconButton";
import { STORAGE_BASE_URL } from "constants";

const { Text } = Typography;

const HISTORY_FETCH_SIZE = 300;
const MAX_HISTORY_RECORDS = 3000;
const SUB_FEATURE_NOTE_PREFIX = "[Sub-Feature:";
const DRAWER_Z_INDEX = 1040;

const RESULT_FILTER_OPTIONS = [
  { label: "All", value: "ALL" },
  { label: "PASS", value: "PASS" },
  { label: "FAIL", value: "FAIL" },
];

const qaAttachmentsDebugEnabled = () => {
  try {
    return window?.localStorage?.getItem("debugQaAttachments") === "1";
  } catch {
    return false;
  }
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

const parseRunNotes = (notes) => {
  const rawNotes = String(notes || "").trim();
  if (!rawNotes.startsWith(SUB_FEATURE_NOTE_PREFIX)) {
    return { subFeature: null, notes: rawNotes };
  }

  const closingIndex = rawNotes.indexOf("]");
  if (closingIndex === -1) {
    return { subFeature: null, notes: rawNotes };
  }

  const subFeature = rawNotes
    .slice(SUB_FEATURE_NOTE_PREFIX.length, closingIndex)
    .trim();
  const cleanNotes = rawNotes.slice(closingIndex + 1).trim();

  return { subFeature: subFeature || null, notes: cleanNotes };
};

const extractAttachments = (run) =>
  run?.attachments ||
  run?.files ||
  run?.fileAttachments ||
  run?.evidence ||
  run?.documents ||
  run?.run?.attachments ||
  run?.run?.files ||
  run?.run?.fileAttachments ||
  run?.run?.documents ||
  run?.run?.evidence ||
  run?.runAttachments ||
  run?.runFiles ||
  [];

const parseRuns = (data) => {
  const source = data?.content || data?.runs || data?.history || data?.items || [];

  return toArray(source).map((run, index) => {
    const parsedNotes = parseRunNotes(run.notes);

    return {
      id: run.id || `run-${index}-${run.testDate || ""}`,
      result: String(run.result || "-").toUpperCase(),
      testDate: run.testDate,
      testedBy: run.testedBy,
      subFeature: run.subFeature || parsedNotes.subFeature,
      notes: parsedNotes.notes,
      versionBuild: run.versionBuild,
      attachments: toArray(extractAttachments(run)),
    };
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMMM D, YYYY h:mm A") : "-";
};

const getRunResultColor = (result = "") => {
  const normalized = String(result).toUpperCase();
  if (normalized === "PASS") return "success";
  if (normalized === "FAIL") return "error";
  return "default";
};

const safeLower = (v) => String(v || "").toLowerCase();

const normalizeAttachment = (attachment) => {
  if (!attachment) return null;
  if (typeof attachment === "string") {
    const trimmed = attachment.trim();
    if (!trimmed) return null;
    const isUrl =
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("data:");
    return {
      fileId: isUrl ? null : trimmed,
      id: trimmed,
      name: trimmed,
      url: isUrl ? trimmed : "",
    };
  }

  const fileId =
    attachment.fileId ||
    attachment.fid ||
    attachment.file_id ||
    attachment.id ||
    attachment.file?.fileId ||
    attachment.file?.id ||
    attachment.file?.fid ||
    null;
  const url =
    attachment.url ||
    attachment.uri ||
    attachment.fileUrl ||
    attachment.downloadUrl ||
    attachment.publicUrl ||
    attachment.signedUrl ||
    attachment.file?.url ||
    attachment.file?.uri ||
    attachment.file?.fileUrl ||
    attachment.file?.downloadUrl ||
    attachment.file?.publicUrl ||
    attachment.file?.signedUrl ||
    "";
  const nameValue =
    attachment.name ||
    attachment.fileName ||
    attachment.filename ||
    attachment.originalName ||
    attachment.key ||
    fileId ||
    "Attachment";

  return {
    ...attachment,
    fileId,
    id: attachment.id || fileId || nameValue,
    name: nameValue,
    url,
  };
};

const isImageAttachment = (attachment = {}) => {
  const value = String(attachment.name || attachment.url || "").toLowerCase();
  return (
    value.includes(".jpg") ||
    value.includes(".jpeg") ||
    value.includes(".png") ||
    value.includes(".gif") ||
    value.includes(".webp") ||
    value.includes(".bmp") ||
    value.includes(".svg")
  );
};

export default function QaTestFeatureHistoryPage() {
  const { appId, featureId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const seededFeature =
    location?.state?.feature && String(location.state.feature.id) === String(featureId)
      ? location.state.feature
      : null;
  const seededApp =
    location?.state?.app && String(location.state.app.id) === String(appId)
      ? location.state.app
      : null;

  const [app, setApp] = useState(seededApp);
  const [feature, setFeature] = useState(seededFeature);

  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState([]);

  const [resultFilter, setResultFilter] = useState("ALL");
  const [subFeatureFilter, setSubFeatureFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRun, setDetailRun] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewIsImage, setPreviewIsImage] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(20);
  const resolveFileUrl = (value) => {
    if (!value) return "";
    const str = String(value).trim();
    if (!str) return "";
    if (str.includes("://")) return str;
    return `${STORAGE_BASE_URL}/files/${str}`;
  };

  const resolveAttachmentUrl = (att) => {
    if (!att) return "";
    const explicit = resolveFileUrl(att.url);
    if (String(att.url || "").includes("://")) return explicit;
    return resolveFileUrl(att.fileId || att.url);
  };

  const querySubFeature = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const value = params.get("subFeature");
    return value ? String(value) : null;
  }, [location.search]);

  useEffect(() => {
    if (querySubFeature) setSubFeatureFilter(querySubFeature);
  }, [querySubFeature]);

  const subFeatureOptions = useMemo(() => {
    const unique = new Set();
    runs.forEach((r) => {
      if (r.subFeature) unique.add(String(r.subFeature));
    });
    const options = Array.from(unique.values())
      .sort((a, b) => String(a).localeCompare(String(b)))
      .map((value) => ({ value, label: value }));
    return [{ value: "ALL", label: "All Sub-Features" }, ...options];
  }, [runs]);

  const filteredRuns = useMemo(() => {
    const term = safeLower(search).trim();
    return runs.filter((r) => {
      if (resultFilter !== "ALL" && r.result !== resultFilter) return false;
      if (subFeatureFilter !== "ALL" && String(r.subFeature) !== String(subFeatureFilter)) {
        return false;
      }
      if (!term) return true;

      const haystack = safeLower(
        [
          r.id,
          r.result,
          r.subFeature,
          r.testedBy,
          r.versionBuild,
          r.notes,
          r.testDate,
        ]
          .filter(Boolean)
          .join(" "),
      );
      return haystack.includes(term);
    });
  }, [runs, resultFilter, subFeatureFilter, search]);

  useEffect(() => {
    setTablePage(1);
  }, [resultFilter, subFeatureFilter, search]);

  useEffect(() => {
    let cancelled = false;

    const fetchMeta = async () => {
      if (!appId || !featureId) return;

      if (!seededApp) {
        try {
          const data = await qaApi.apps.get(appId);
          if (!cancelled) setApp(data);
        } catch {
          // ignore
        }
      }

      if (!seededFeature) {
        try {
          const data = await qaApi.features.get(appId, featureId);
          if (!cancelled) setFeature(data);
        } catch {
          // ignore
        }
      }
    };

    fetchMeta();
    return () => {
      cancelled = true;
    };
  }, [appId, featureId, seededApp, seededFeature]);

  useEffect(() => {
    let cancelled = false;

    const fetchAllHistory = async () => {
      if (!appId || !featureId) return;
      setLoading(true);
      try {
        const all = [];
        let page = 0;

        while (all.length < MAX_HISTORY_RECORDS) {
          if (qaAttachmentsDebugEnabled()) {
            // eslint-disable-next-line no-console
            console.log("[qa-attachments] feature-history:fetch", {
              appId,
              featureId,
              page,
              size: HISTORY_FETCH_SIZE,
            });
          }

          const data = await qaApi.features.getHistory(appId, featureId, {
            page,
            size: HISTORY_FETCH_SIZE,
          });

          const pageOffset = page * HISTORY_FETCH_SIZE;
          const pageRuns = parseRuns(data).map((run, index) => ({
            ...run,
            id:
              run.id ||
              `run-${featureId || "feature"}-${pageOffset + index}-${run.testDate || ""}`,
          }));

          if (qaAttachmentsDebugEnabled()) {
            const sample = pageRuns.find((r) => Array.isArray(r?.attachments) && r.attachments.length);
            // eslint-disable-next-line no-console
            console.log("[qa-attachments] feature-history:response", {
              page,
              received: Array.isArray(pageRuns) ? pageRuns.length : 0,
              sampleRunId: sample?.id || null,
              sampleAttachments: sample?.attachments || [],
            });
          }

          all.push(...pageRuns);

          const contentLength =
            (Array.isArray(data?.content) && data.content.length) || pageRuns.length;
          const totalPages =
            typeof data?.totalPages === "number" ? Number(data.totalPages) : null;

          if (contentLength < HISTORY_FETCH_SIZE) break;
          if (totalPages != null && page + 1 >= totalPages) break;

          page += 1;
          if (page > 50) break;
        }

        if (!cancelled) setRuns(all);
      } catch (err) {
        message.error("Failed to load feature history");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAllHistory();
    return () => {
      cancelled = true;
    };
  }, [appId, featureId]);

  const columns = useMemo(
    () => [
      {
        title: "Result",
        dataIndex: "result",
        key: "result",
        width: 90,
        render: (value) => <Tag color={getRunResultColor(value)}>{value}</Tag>,
      },
      {
        title: "Test Date",
        dataIndex: "testDate",
        key: "testDate",
        width: 180,
        render: (value) => formatDateTime(value),
      },
      {
        title: "Sub-Feature",
        dataIndex: "subFeature",
        key: "subFeature",
        width: 200,
        render: (value) => value || "-",
      },
      {
        title: "Notes",
        dataIndex: "notes",
        key: "notes",
        render: (value) => (
          <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {value || "-"}
          </div>
        ),
      },
      {
        title: "",
        key: "spacer",
        width: 1,
        render: () => null,
      },
    ],
    [],
  );

  const title = useMemo(() => {
    const base = feature?.name ? `QA History: ${feature.name}` : "QA Feature History";
    if (querySubFeature) return `${base} > ${querySubFeature}`;
    return base;
  }, [feature?.name, querySubFeature]);

  const handleGoBack = () => {
    const from = location?.state?.from;
    const fromState = location?.state?.fromState;
    if (typeof from === "string" && from.trim()) {
      navigate(from, { state: fromState || (seededApp ? { app: seededApp } : undefined) });
      return;
    }
    navigate(-1);
  };

  const openAttachmentPreview = (attachment, url) => {
    if (!url) return;
    const name = attachment?.name || attachment?.fileId || "Attachment";
    const isImage = isImageAttachment({ ...attachment, url });
    if (!isImage) {
      window.open(url, "_blank", "noreferrer");
      return;
    }
    setPreviewTitle(name);
    setPreviewUrl(url);
    setPreviewIsImage(true);
    setPreviewOpen(true);

    if (qaAttachmentsDebugEnabled()) {
      // eslint-disable-next-line no-console
      console.log("[qa-attachments] feature-history:preview", {
        name,
        fileId: attachment?.fileId || null,
        url,
      });
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items mb-3 mt-3 gap-3">
        <WidgetSectionTitle
          icon="bug"
          title={title}
          subtitle={
            app?.name
              ? `App: ${app.name} • Filter runs by PASS/FAIL and sub-feature.`
              : "Filter runs by PASS/FAIL and sub-feature."
          }
          className="flex-1"
        />
        <div className="d-flex justify-content-end mb-4">
          <IconButton
            variant="primary"
            size="sm"
            className="text-sm me-2"
            onClick={handleGoBack}
          >
            Go Back
          </IconButton>
        </div>
      </div>

      <Card style={{ borderRadius: 10 }}>
        <Row gutter={[10, 10]} align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Select
              value={resultFilter}
              onChange={setResultFilter}
              options={RESULT_FILTER_OPTIONS}
              style={{ width: 140 }}
            />
          </Col>

          <Col>
            <Select
              showSearch
              value={subFeatureFilter}
              onChange={setSubFeatureFilter}
              options={subFeatureOptions}
              optionFilterProp="label"
              style={{ width: 260 }}
            />
          </Col>

          <Col>
            <Input
              allowClear
              placeholder="Search notes, tester..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 320 }}
            />
          </Col>

          <Col style={{ marginLeft: "auto" }}>
            <Space>
              <Text type="secondary">
                Showing {filteredRuns.length} / {runs.length}
              </Text>
            </Space>
          </Col>
        </Row>

        <Table
          rowKey={(record, index) =>
            `${record?.id || "run"}-${record?.testDate || "no-date"}-${record?.subFeature || "no-subfeature"}-${index}`
          }
          columns={columns}
          dataSource={filteredRuns}
          loading={loading}
          size="small"
          className="custom-table"
          tableLayout="fixed"
          onRow={(record) => ({
            onClick: () => {
              setDetailRun(record);
              setDetailOpen(true);
            },
          })}
          pagination={{
            current: tablePage,
            pageSize: tablePageSize,
            total: filteredRuns.length,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            onChange: (nextPage, nextSize) => {
              const resolvedSize = nextSize || tablePageSize;
              setTablePageSize(resolvedSize);
              setTablePage(nextPage || 1);
            },
          }}
          locale={{
            emptyText: loading ? null : <Empty description="No history found" />,
          }}
        />
      </Card>

      <Drawer
        title={
          feature?.name
            ? `Run Details: ${feature.name}`
            : "Run Details"
        }
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setDetailRun(null);
        }}
        width={560}
        destroyOnClose
        zIndex={DRAWER_Z_INDEX}
      >
        {detailRun ? (
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
            <Space wrap>
              <Tag color={getRunResultColor(detailRun.result)}>{detailRun.result}</Tag>
              <Text type="secondary">{formatDateTime(detailRun.testDate)}</Text>
            </Space>

            <Space wrap size={12}>
              <Text type="secondary">Tester: {detailRun.testedBy || "-"}</Text>
              <Text type="secondary">Build: {detailRun.versionBuild || "-"}</Text>
            </Space>

            <div>
              <Text strong>Sub-feature</Text>
              <div>
                <Text>{detailRun.subFeature || "-"}</Text>
              </div>
            </div>

            <div>
              <Text strong>Notes</Text>
              <div>
                <Text type="secondary">{detailRun.notes || "-"}</Text>
              </div>
            </div>

            <div>
              <Text strong>Attachments</Text>
              <div style={{ marginTop: 8 }}>
                {toArray(detailRun.attachments).map(normalizeAttachment).filter(Boolean)
                  .length === 0 ? (
                  <Text type="secondary">-</Text>
                ) : (
                  <Space wrap>
                    {toArray(detailRun.attachments)
                      .map(normalizeAttachment)
                      .filter(Boolean)
                      .map((att) => {
                        const fileId = att.fileId;
                        const url = resolveAttachmentUrl(att);
                        const key = fileId || att.id || att.name;

                        if (qaAttachmentsDebugEnabled()) {
                          // eslint-disable-next-line no-console
                          console.log("[qa-attachments] feature-history:item", {
                            fileId,
                            name: att?.name,
                            rawUrl: att?.url,
                            resolvedUrl: url,
                          });
                        }

                        if (isImageAttachment({ ...att, url }) && url) {
                          // eslint-disable-next-line jsx-a11y/alt-text
                          return (
                            <img
                              key={key}
                              src={url}
                              onClick={() => openAttachmentPreview(att, url)}
                              style={{
                                width: 84,
                                height: 84,
                                objectFit: "cover",
                                borderRadius: 8,
                                border: "1px solid #eee",
                                cursor: "pointer",
                              }}
                            />
                          );
                        }

                        if (url) {
                          return (
                            <Button
                              key={key}
                              type="link"
                              onClick={() => openAttachmentPreview(att, url)}
                              style={{ paddingInline: 0 }}
                            >
                              {att.name || fileId}
                            </Button>
                          );
                        }

                        return (
                          <Text key={key} type="secondary" style={{ fontSize: 12 }}>
                            {att.name || fileId || "-"}
                          </Text>
                        );
                      })}
                  </Space>
                )}
              </div>
            </div>
          </Space>
        ) : (
          <Empty description="No details to show" />
        )}
      </Drawer>

      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        width={900}
        zIndex={DRAWER_Z_INDEX + 10}
        destroyOnClose
      >
        {previewIsImage && previewUrl ? (
          <div style={{ textAlign: "center" }}>
            <Image
              src={previewUrl}
              alt={previewTitle}
              style={{ maxHeight: "70vh", objectFit: "contain" }}
            />
          </div>
        ) : (
          <Empty description="No preview available" />
        )}
      </Modal>
    </>
  );
}
